import { Trans, withI18n } from '@lingui/react'
import { oneOf } from 'prop-types'
import { compose } from 'redux'

import Button from 'views/components/atoms/Button'
import Form from 'views/components/atoms/Form'
import InputTextWithWithFloatingLabel from 'views/components/molecules/InputTextWithFloatingLabel'
import Notification from 'views/components/molecules/Notification'
import Select from 'views/components/molecules/Select'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import withMountPoints from 'views/enhancers/withMountPoints'
import { mountPointsType, ProductType } from 'views/utils/propTypes'
import { pickRequired } from 'views/utils/propTypeUtils'

import { getDays, HOURS, MINUTES, ORDER_TYPE, PRODUCT_TYPE } from './helpers'

const CALLBACK_FORM = {
  PZN: 'productCode',
  ORDER_ID: 'orderId',
  FIRST_NAME: '§callbackForm.firstName',
  LAST_NAME: '§callbackForm.lastName',
  PHONE: '§callbackForm.phone',
  EMAIL: '§callbackForm.email',
  WEEKDAY: '§callbackForm.weekday',
  HOUR: '§callbackForm.hour',
  MINUTE: '§callbackForm.minute',
  TYPE: 'type',
}

const ProductConsultationCallback = ({ name, id, i18n, type, mountPoints }) => {
  const DAYS = getDays(i18n)

  return (
    <>
      {type === 'product' ? (
        <TransWithHtml
          id="product.consultation.callback.headline"
          element="p"
          values={{ name }}
        />
      ) : (
        <TransWithHtml
          id="product.consultation.orders.headline"
          element="p"
          values={{ name }}
        />
      )}
      <Form
        className="o-ProductConsultationCallbackForm"
        name="callback-service-form"
        data-clientside-hook="formValidation productConsultationCallbackForm"
      >
        <input
          type="hidden"
          name={type === 'product' ? CALLBACK_FORM.PZN : CALLBACK_FORM.ORDER_ID}
          value={id}
        />
        <input type="hidden" name={CALLBACK_FORM.TYPE} value={type} />
        <ul className="l-grid l-grid--tight">
          <li className="l-grid__item l-col--6-of-12 u-margin--bottom">
            <InputTextWithWithFloatingLabel
              name={CALLBACK_FORM.FIRST_NAME}
              inputType="text"
              required
            >
              <Trans id="general.label.firstName" />
            </InputTextWithWithFloatingLabel>
          </li>
          <li className="l-grid__item l-col--6-of-12 u-margin--bottom">
            <InputTextWithWithFloatingLabel
              name={CALLBACK_FORM.LAST_NAME}
              inputType="text"
              required
            >
              <Trans id="general.label.surname" />
            </InputTextWithWithFloatingLabel>
          </li>
          <li className="l-grid__item u-margin--bottom">
            <InputTextWithWithFloatingLabel
              name={CALLBACK_FORM.PHONE}
              inputType="tel"
              required
            >
              <Trans id="callbackForm.label.phoneNumber" />
            </InputTextWithWithFloatingLabel>
          </li>
          <li className="l-grid__item u-margin-x-large--bottom">
            <InputTextWithWithFloatingLabel
              name={CALLBACK_FORM.EMAIL}
              inputType="email"
            >
              <Trans id="general.label.email" />
            </InputTextWithWithFloatingLabel>
            <p className="u-no-margin u-padding-small--top">
              <small>
                <Trans id="product.consultation.tooltip.email" />
              </small>
            </p>
          </li>
          <li className="l-grid__item u-margin--bottom">
            <ul className="a-list-reset l-flex l-flex--wrap">
              <li className="l-col--12-of-12@small-tiny u-margin--bottom@small-tiny">
                <div className="a-label u-margin--bottom">
                  <Trans id="general.label.weekday" />
                </div>
                <ul className="a-list-reset l-flex">
                  <li className="u-margin--right o-ProductConsultationCallback__daySelect-wrapper">
                    <Select {...DAYS} name={CALLBACK_FORM.WEEKDAY} smaller />
                  </li>
                </ul>
              </li>
              <li className="l-col--12-of-12@small-tiny">
                <div className="a-label u-margin--bottom">
                  <Trans id="general.label.time" />
                </div>
                <ul className="a-list-reset l-flex">
                  <li className="u-margin--right">
                    <Select {...HOURS} name={CALLBACK_FORM.HOUR} smaller />
                  </li>
                  <li className="u-margin--right">
                    <Select {...MINUTES} name={CALLBACK_FORM.MINUTE} smaller />
                  </li>
                </ul>
              </li>
            </ul>
            <p className="u-no-margin u-padding-small--top">
              <small>
                <Trans id="product.consultation.tooltip.time" />
              </small>
            </p>
          </li>
          <li className="l-grid__item u-margin--bottom">
            <p>
              <Trans id="general.form.label.mandatoryFieldIndicator" />
            </p>
            <TransWithHtml
              id="general.label.privacyPolicyAgreement"
              element="p"
              values={{ url: mountPoints.getPrivacySecurity() }}
            />
          </li>
          <li
            className="l-grid__item u-hidden u-margin--bottom"
            data-clientside-hook="productConsultationCallbackError"
            aria-hidden
          >
            <Notification
              type="error"
              label={i18n._('product.consultation.callback.error')}
            />
          </li>
          <li className="l-grid__item u-margin--bottom">
            <div className="u-text-align--right">
              <Button name="CallbackForm-callback" inline type="submit">
                <Trans id="product.consultation.button.submitData" />
              </Button>
            </div>
          </li>
        </ul>
      </Form>
      <div
        className="u-hidden"
        data-clientside-hook="productConsultationCallbackSuccess"
      >
        <Notification
          type="success"
          label={i18n._('product.consultation.callback.success')}
        />
      </div>
    </>
  )
}

ProductConsultationCallback.propTypes = {
  ...pickRequired(ProductType, ['name', 'id']),
  type: oneOf([PRODUCT_TYPE, ORDER_TYPE]),
  mountPoints: mountPointsType.isRequired,
}

ProductConsultationCallback.defaultProps = {
  type: PRODUCT_TYPE,
}

export default compose(withI18n(), withMountPoints)(ProductConsultationCallback)
